// src/components/ui/ModernDataTable.js - Complete version with itemIndex for structured photo naming
import { Edit, HelpCircle, Plus, X } from 'lucide-react';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { theme } from '../../theme/colors';
import PhotoUpload from './PhotoUpload';

const ModernDataTable = forwardRef(({
      data = [],
      setData,
      fields = [],
      addButtonText = "Add Item",
      emptyMessage = "No items added yet.",
      removeButtonLabel = "Remove",
      editButtonLabel = "Edit",
      showEditButton = true,
      truncateLines = 0,
      maxHeight = "60vh",
      suppressTitle = false,
      enablePhotoUpload = false,
      referenceNo,
      sectionName
}, ref) => {
      const [showForm, setShowForm] = useState(false);
      const [currentItem, setCurrentItem] = useState({});
      const [editingItemId, setEditingItemId] = useState(null);
      const [errors, setErrors] = useState({});
      // Removed openHints state (unused) to satisfy eslint no-unused-vars
      // const [openHints, setOpenHints] = useState({});
      const [hoveredHint, setHoveredHint] = useState(null);

      const getCurrentDateTime = () => {
            const now = new Date();
            const timezoneOffset = now.getTimezoneOffset() * 60000;
            const localISOTime = new Date(now.getTime() - timezoneOffset).toISOString();
            return localISOTime.slice(0, 16);
      };

      const initializeFormData = () => {
            const initialData = {};
            fields.forEach(field => {
                  if (field.type === 'grid' && field.fields) {
                        field.fields.forEach(subField => {
                              if (subField.type === 'datetime') {
                                    initialData[subField.name] = getCurrentDateTime();
                              } else {
                                    initialData[subField.name] = subField.type === 'checkbox' ? false : '';
                              }
                        });
                  } else {
                        if (field.type === 'datetime') {
                              initialData[field.name] = getCurrentDateTime();
                        } else {
                              initialData[field.name] = field.type === 'checkbox' ? false : '';
                        }
                  }
            });
            // Always initialize photos array
            initialData.photos = [];
            return initialData;
      };

      // Calculate the item index for structured photo naming
      const getItemIndex = () => {
            if (editingItemId !== null) {
                  // If editing, find the position of the item being edited
                  const editingIndex = data.findIndex(item => item.id === editingItemId);
                  return editingIndex >= 0 ? editingIndex + 1 : data.length + 1;
            } else {
                  // If adding new, it will be the next item
                  return data.length + 1;
            }
      };

      const isFieldVisible = (field, currentData) => {
            if (field.condition && typeof field.condition === 'function') {
                  return field.condition(currentData);
            }
            if (!field.hideUntil) return true;
            const { condition } = field.hideUntil;
            if (typeof condition === 'function') {
                  return condition(currentData);
            }
            if (condition && condition.field && condition.value !== undefined) {
                  return currentData[condition.field] === condition.value;
            }
            return true;
      };

      const validateForm = (data) => {
            const newErrors = {};
            fields.forEach(field => {
                  if (field.type === 'grid' && field.fields) {
                        field.fields.forEach(subField => {
                              if (!isFieldVisible(subField, data)) return;
                              if (subField.required && !data[subField.name]) {
                                    newErrors[subField.name] = subField.validationMessage || getDefaultValidationMessage(subField, 'required');
                              }
                              if (subField.conditionalRequired) {
                                    const condition = subField.conditionalRequired.condition;
                                    let conditionMet = false;
                                    if (typeof condition === 'function') {
                                          conditionMet = condition(data);
                                    } else if (condition && condition.field && condition.value !== undefined) {
                                          conditionMet = data[condition.field] === condition.value;
                                    }
                                    if (conditionMet && !data[subField.name]) {
                                          newErrors[subField.name] = subField.conditionalRequired.message || getDefaultValidationMessage(subField, 'conditionalRequired', condition);
                                    }
                              }
                              const fieldError = validateFieldValue(subField, data[subField.name], data);
                              if (fieldError) {
                                    newErrors[subField.name] = fieldError;
                              }
                        });
                  } else {
                        if (!isFieldVisible(field, data)) return;
                        if (field.required && !data[field.name]) {
                              newErrors[field.name] = field.validationMessage || getDefaultValidationMessage(field, 'required');
                        }
                        if (field.conditionalRequired) {
                              const condition = field.conditionalRequired.condition;
                              let conditionMet = false;
                              if (typeof condition === 'function') {
                                    conditionMet = condition(data);
                              } else if (condition && condition.field && condition.value !== undefined) {
                                    conditionMet = data[condition.field] === condition.value;
                              }
                              if (conditionMet && !data[field.name]) {
                                    newErrors[field.name] = field.conditionalRequired.message || getDefaultValidationMessage(field, 'conditionalRequired', condition);
                              }
                        }
                        const fieldError = validateFieldValue(field, data[field.name], data);
                        if (fieldError) {
                              newErrors[field.name] = fieldError;
                        }
                  }
            });
            return newErrors;
      };

      const getDefaultValidationMessage = (field, validationType, condition) => {
            const fieldTypeName = field.type === 'datetime' ? 'date and time' :
                  field.type === 'dropdown' ? 'selection' :
                        field.type === 'number' ? 'number' :
                              field.type === 'textarea' ? 'description' : 'value';
            switch (validationType) {
                  case 'required':
                        return `Please enter ${field.label.toLowerCase()} - this field is required`;
                  case 'conditionalRequired':
                        if (condition && typeof condition === 'function') {
                              return `Please enter ${field.label.toLowerCase()} - this field is required based on your previous selection`;
                        } else if (condition && condition.field && condition.value) {
                              return `Please enter ${field.label.toLowerCase()} - this field is required when ${condition.field} is "${condition.value}"`;
                        }
                        return `Please enter ${field.label.toLowerCase()} - this field is required based on your selection`;
                  default:
                        return `Please enter a valid ${fieldTypeName} for ${field.label.toLowerCase()}`;
            }
      };

      const validateFieldValue = (field, value, allData) => {
            if (!isFieldVisible(field, allData) || !value) return null;
            switch (field.type) {
                  case 'number':
                        const numValue = parseFloat(value);
                        if (isNaN(numValue)) {
                              return `${field.label} must be a valid number`;
                        }
                        if (field.min !== undefined && numValue < field.min) {
                              return `${field.label} must be at least ${field.min}`;
                        }
                        if (field.max !== undefined && numValue > field.max) {
                              return `${field.label} must be no more than ${field.max}`;
                        }
                        break;
                  case 'text':
                        if (field.minLength && value.length < field.minLength) {
                              return `${field.label} must be at least ${field.minLength} characters long`;
                        }
                        if (field.maxLength && value.length > field.maxLength) {
                              return `${field.label} must be no more than ${field.maxLength} characters long`;
                        }
                        if (field.pattern && !new RegExp(field.pattern).test(value)) {
                              return field.patternMessage || `${field.label} format is not valid`;
                        }
                        break;
                  case 'textarea':
                        if (field.minLength && value.length < field.minLength) {
                              return `${field.label} must be at least ${field.minLength} characters long`;
                        }
                        if (field.maxLength && value.length > field.maxLength) {
                              return `${field.label} must be no more than ${field.maxLength} characters long`;
                        }
                        break;
                  case 'datetime':
                        const dateValue = new Date(value);
                        if (isNaN(dateValue.getTime())) {
                              return `Please enter a valid date and time for ${field.label}`;
                        }
                        if (field.minDate && dateValue < new Date(field.minDate)) {
                              return `${field.label} cannot be earlier than ${new Date(field.minDate).toLocaleDateString()}`;
                        }
                        if (field.maxDate && dateValue > new Date(field.maxDate)) {
                              return `${field.label} cannot be later than ${new Date(field.maxDate).toLocaleDateString()}`;
                        }
                        break;
                  case 'dropdown':
                        if (field.options && !field.options.find(opt => opt.value === value)) {
                              return `Please select a valid option for ${field.label}`;
                        }
                        break;
            }
            return null;
      };

      const clearHiddenFields = (data) => {
            const clearedData = { ...data };
            const checkAndClearFields = (fieldsToCheck) => {
                  fieldsToCheck.forEach(field => {
                        if (field.type === 'grid' && field.fields) {
                              checkAndClearFields(field.fields);
                        } else {
                              if (!isFieldVisible(field, clearedData)) {
                                    if (field.type === 'checkbox') {
                                          clearedData[field.name] = false;
                                    } else {
                                          clearedData[field.name] = '';
                                    }
                              }
                        }
                  });
            };
            checkAndClearFields(fields);
            return clearedData;
      };

      const handleInputChange = (fieldName, value) => {
            setCurrentItem(prev => {
                  const updated = { ...prev, [fieldName]: value };
                  const clearedData = clearHiddenFields(updated);
                  return clearedData;
            });
            if (errors[fieldName]) {
                  setErrors(prev => ({ ...prev, [fieldName]: null }));
            }
            const field = fields.find(f => f.name === fieldName) ||
                  fields.flatMap(f => f.type === 'grid' ? f.fields : []).find(f => f.name === fieldName);
            if (field && value && value.toString().trim() !== '') {
                  const fieldError = validateFieldValue(field, value, { ...currentItem, [fieldName]: value });
                  if (fieldError) {
                        setErrors(prev => ({ ...prev, [fieldName]: fieldError }));
                  }
            }
      };

      const handleAddItem = () => {
            const validationErrors = validateForm(currentItem);
            if (Object.keys(validationErrors).length > 0) {
                  setErrors(validationErrors);
                  return;
            }
            if (editingItemId !== null) {
                  const updatedItem = {
                        ...currentItem,
                        id: editingItemId,
                        displayValues: generateDisplayValues(currentItem)
                  };
                  setData(prev => prev.map(item =>
                        item.id === editingItemId ? updatedItem : item
                  ));
                  setEditingItemId(null);
            } else {
                  const newItem = {
                        id: Date.now(),
                        ...currentItem,
                        displayValues: generateDisplayValues(currentItem)
                  };
                  setData(prev => [...prev, newItem]);
            }
            setCurrentItem(initializeFormData());
            setShowForm(false);
            setErrors({});
      };

      const generateDisplayValues = (item) => {
            const values = [];
            fields.forEach(field => {
                  if (field.type === 'grid' && field.fields) {
                        field.fields.forEach(subField => {
                              if (!subField.hideInTable) {
                                    values.push(formatDisplayValue(subField, item[subField.name], item));
                              }
                        });
                  } else if (!field.hideInTable) {
                        values.push(formatDisplayValue(field, item[field.name], item));
                  }
            });
            return values;
      };

      const formatDisplayValue = (field, value, itemData) => {
            if (field.displayTransform && typeof field.displayTransform === 'function') {
                  return field.displayTransform(value, itemData);
            }
            if (field.type === 'checkbox') {
                  return value ? 'Yes' : 'No';
            } else if (field.type === 'datetime') {
                  if (value) {
                        const date = new Date(value);
                        return date.toLocaleString();
                  } else {
                        return '';
                  }
            } else if (field.type === 'dropdown') {
                  if (field.options && Array.isArray(field.options)) {
                        const option = field.options.find(opt => opt.value === value);
                        return option ? option.label : value || '';
                  }
                  return value || '';
            } else if (field.type === 'number') {
                  return value ? String(value) : '';
            } else {
                  return value || '';
            }
      };

      // Expose tryCommit method to parent via ref
      useImperativeHandle(ref, () => ({
            tryCommit: () => {
                  // If form is not visible, nothing to commit
                  if (!showForm) {
                        return Promise.resolve(true);
                  }

                  // Validate the current form
                  const validationErrors = validateForm(currentItem);
                  if (Object.keys(validationErrors).length > 0) {
                        // Show validation errors and prevent navigation
                        setErrors(validationErrors);
                        return Promise.resolve(false);
                  }

                  // Form is valid - commit it
                  if (editingItemId !== null) {
                        const updatedItem = {
                              ...currentItem,
                              id: editingItemId,
                              displayValues: generateDisplayValues(currentItem)
                        };
                        setData(prev => prev.map(item =>
                              item.id === editingItemId ? updatedItem : item
                        ));
                        setEditingItemId(null);
                  } else {
                        const newItem = {
                              id: Date.now(),
                              ...currentItem,
                              displayValues: generateDisplayValues(currentItem)
                        };
                        setData(prev => [...prev, newItem]);
                  }

                  // Reset form state
                  setCurrentItem(initializeFormData());
                  setShowForm(false);
                  setErrors({});

                  return Promise.resolve(true);
            }
      }));

      const generateHeaders = () => {
            const headers = [];
            fields.forEach(field => {
                  if (field.type === 'grid' && field.fields) {
                        field.fields.forEach(subField => {
                              if (!subField.hideInTable) {
                                    headers.push(subField.header || subField.label);
                              }
                        });
                  } else if (!field.hideInTable) {
                        headers.push(field.header || field.label);
                  }
            });
            return headers;
      };

      const handleRemoveItem = (id) => {
            setData(prev => prev.filter(item => item.id !== id));
      };

      const handleEditItem = (id) => {
            const itemToEdit = data.find(item => item.id === id);
            if (itemToEdit) {
                  const { id: itemId, displayValues, ...itemData } = itemToEdit;
                  setCurrentItem(itemData);
                  setEditingItemId(id);
                  setShowForm(true);
                  setErrors({});
                  // setOpenHints({}); // removed unused state cleanup
                  setHoveredHint(null);
            }
      };

      const handleCancelForm = () => {
            setShowForm(false);
            setCurrentItem(initializeFormData());
            setEditingItemId(null);
            setErrors({});
            // setOpenHints({}); // removed unused state cleanup
            setHoveredHint(null);
      };

      const renderField = (field) => {
            if (field.condition && !field.condition(currentItem)) {
                  return null;
            }
            if (!isFieldVisible(field, currentItem)) {
                  return null;
            }
            if (field.type === 'grid') {
                  return (
                        <div key={field.name} style={{
                              display: 'grid',
                              gridTemplateColumns: field.gridColumns || 'repeat(auto-fit, minmax(250px, 1fr))',
                              gap: '1rem',
                              marginBottom: '1rem'
                        }}>
                              {field.fields.map(subField => renderField(subField))}
                        </div>
                  );
            }

            const getFieldStyle = (fieldName) => ({
                  width: "100%",
                  padding: "0.875rem 1rem",
                  backgroundColor: "white",
                  border: `1px solid ${errors[fieldName] ? '#ef4444' : '#d1d5db'}`,
                  borderRadius: "0.375rem",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  fontSize: "0.875rem",
                  outline: "none",
                  transition: "all 0.2s ease",
                  boxSizing: "border-box"
            });

            const handleFocus = (e) => {
                  e.target.style.borderColor = "#06b6d4";
                  e.target.style.boxShadow = "0 0 0 3px rgba(6, 182, 212, 0.2)";
            };

            const handleBlur = (e, fieldName) => {
                  e.target.style.borderColor = errors[fieldName] ? '#ef4444' : "#d1d5db";
                  e.target.style.boxShadow = errors[fieldName] ? "none" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
            };

            const getCurrentValue = () => {
                  if (field.type === 'datetime') {
                        return currentItem[field.name] || getCurrentDateTime();
                  }
                  if (field.type === 'checkbox') {
                        return currentItem[field.name] || false;
                  }
                  return currentItem[field.name] || '';
            };

            const hasValue = () => {
                  const value = getCurrentValue();
                  if (field.type === 'checkbox') return false;
                  if (field.type === 'datetime') return true;
                  return value && value.toString().trim().length > 0;
            };

            return (
                  <div key={field.name} style={{ marginBottom: '1.5rem' }}>
                        {field.type === 'checkbox' ? (
                              <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '0.75rem',
                                    padding: '0.5rem 0'
                              }}>
                                    <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.5rem',
                                          flex: 1
                                    }}>
                                          <span style={{
                                                fontSize: '0.875rem',
                                                color: '#374151',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                lineHeight: '1.4'
                                          }}
                                                onClick={() => handleInputChange(field.name, !getCurrentValue())}
                                          >
                                                {field.checkboxLabel || field.label}
                                                {field.required && <span style={{ color: "#ef4444" }}> *</span>}
                                          </span>
                                          {field.hint && (
                                                <div style={{ position: "relative", display: "inline-block", flexShrink: 0 }}>
                                                      <HelpCircle
                                                            size={14}
                                                            style={{
                                                                  color: "#6b7280",
                                                                  cursor: "help",
                                                                  transition: "color 0.2s ease"
                                                            }}
                                                            onClick={() => setHoveredHint(field.name)}
                                                      />
                                                </div>
                                          )}
                                    </div>
                                    <input
                                          type="checkbox"
                                          checked={getCurrentValue()}
                                          onChange={(e) => handleInputChange(field.name, e.target.checked)}
                                          style={{
                                                width: '1rem',
                                                height: '1rem',
                                                cursor: 'pointer',
                                                flexShrink: 0
                                          }}
                                    />
                              </div>
                        ) : (
                              <div style={{ position: "relative" }}>
                                    <div style={{
                                          position: "absolute",
                                          top: "-0.5rem",
                                          left: "0.75rem",
                                          backgroundColor: "white",
                                          padding: "0 0.5rem",
                                          fontSize: "0.875rem",
                                          fontWeight: "500",
                                          color: "#64748b",
                                          zIndex: 1,
                                          lineHeight: "1.2",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.25rem"
                                    }}>
                                          <span>
                                                {field.label}
                                                {field.required && <span style={{ color: "#ef4444" }}> *</span>}
                                          </span>
                                          {field.hint && (
                                                <div style={{ position: "relative", display: "inline-block" }}>
                                                      <HelpCircle
                                                            size={14}
                                                            style={{
                                                                  color: "#6b7280",
                                                                  cursor: "help",
                                                                  transition: "color 0.2s ease"
                                                            }}
                                                            onClick={() => setHoveredHint(field.name)}
                                                      />
                                                </div>
                                          )}
                                    </div>

                                    {field.type === 'textarea' ? (
                                          <textarea
                                                value={getCurrentValue()}
                                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                placeholder={!hasValue() ? field.placeholder : ""}
                                                rows={3}
                                                style={{
                                                      ...getFieldStyle(field.name),
                                                      resize: 'vertical',
                                                      minHeight: '4.5rem',
                                                      fontFamily: 'inherit'
                                                }}
                                                onFocus={handleFocus}
                                                onBlur={(e) => handleBlur(e, field.name)}
                                          />
                                    ) : field.type === 'datetime' ? (
                                          <input
                                                type="datetime-local"
                                                value={getCurrentValue()}
                                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                style={getFieldStyle(field.name)}
                                                onFocus={handleFocus}
                                                onBlur={(e) => handleBlur(e, field.name)}
                                          />
                                    ) : field.type === 'dropdown' ? (
                                          <select
                                                value={getCurrentValue()}
                                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                style={{
                                                      ...getFieldStyle(field.name),
                                                      cursor: 'pointer',
                                                      paddingRight: '2.5rem'
                                                }}
                                                onFocus={handleFocus}
                                                onBlur={(e) => handleBlur(e, field.name)}
                                          >
                                                <option value="">{field.placeholder || 'Select an option...'}</option>
                                                {field.options && field.options.map((option) => (
                                                      <option key={option.value} value={option.value}>
                                                            {option.label}
                                                      </option>
                                                ))}
                                          </select>
                                    ) : field.type === 'number' ? (
                                          <input
                                                type="number"
                                                value={getCurrentValue()}
                                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                placeholder={!hasValue() ? field.placeholder : ""}
                                                min={field.min}
                                                max={field.max}
                                                step={field.step}
                                                style={getFieldStyle(field.name)}
                                                onFocus={handleFocus}
                                                onBlur={(e) => handleBlur(e, field.name)}
                                          />
                                    ) : (
                                          <input
                                                type="text"
                                                value={getCurrentValue()}
                                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                placeholder={!hasValue() ? field.placeholder : ""}
                                                style={getFieldStyle(field.name)}
                                                onFocus={handleFocus}
                                                onBlur={(e) => handleBlur(e, field.name)}
                                          />
                                    )}
                              </div>
                        )}

                        {errors[field.name] && (
                              <div style={{
                                    marginTop: '0.25rem',
                                    color: '#ef4444',
                                    fontSize: '0.75rem'
                              }}>
                                    {errors[field.name]}
                              </div>
                        )}
                  </div>
            );
      };

      const truncateText = (text, maxLines) => {
            if (!maxLines || !text) return text;
            const words = text.split(' ');
            const wordsPerLine = 8;
            const maxWords = maxLines * wordsPerLine;
            if (words.length <= maxWords) return text;
            return words.slice(0, maxWords).join(' ') + '...';
      };

      const getTruncatedStyle = (truncateLines) => {
            if (!truncateLines) return {};
            return {
                  maxHeight: `${truncateLines * 1.5}rem`,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: truncateLines,
                  WebkitBoxOrient: 'vertical'
            };
      };

      const headers = generateHeaders();
      const gridTemplate = headers.length > 0
            ? `repeat(${headers.length}, minmax(0, 1fr)) auto`
            : '1fr auto';

      return (
            <div style={{ padding: '1.5rem' }}>
                  {!suppressTitle && (
                        <div style={{ marginBottom: '1.5rem' }}>
                              <h3 style={{
                                    fontSize: '1.25rem',
                                    fontWeight: '600',
                                    marginBottom: '0.5rem',
                                    color: '#1f2937'
                              }}>
                                    {addButtonText.replace('Add ', '')}
                              </h3>
                        </div>
                  )}

                  <div style={{
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.borderRadius.lg,
                        overflow: 'hidden',
                        backgroundColor: theme.colors.cardBackground,
                        boxShadow: theme.shadows.sm,
                        maxHeight: maxHeight,
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: '1rem'
                  }}>
                        {headers.length > 0 && (
                              <div style={{
                                    backgroundColor: '#f8fafc',
                                    borderBottom: `1px solid ${theme.colors.border}`,
                                    padding: '1rem 1.25rem',
                                    flexShrink: 0
                              }}>
                                    <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: gridTemplate,
                                          gap: '1rem',
                                          fontSize: '0.875rem',
                                          fontWeight: '600',
                                          color: theme.colors.text.primary,
                                          alignItems: 'center'
                                    }}>
                                          {headers.map((header, index) => (
                                                <div
                                                      key={index}
                                                      style={{
                                                            padding: '0 0.5rem',
                                                            textAlign: 'left',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            minWidth: 0
                                                      }}
                                                >
                                                      {header}
                                                </div>
                                          ))}
                                          <div style={{
                                                textAlign: 'right',
                                                padding: '0 0.5rem',
                                                minWidth: showEditButton ? '120px' : '80px'
                                          }}>
                                                Actions
                                          </div>
                                    </div>
                              </div>
                        )}

                        <div style={{
                              flex: 1,
                              overflowY: 'auto',
                              minHeight: 0
                        }}>
                              {data.length === 0 ? (
                                    <div style={{
                                          textAlign: 'center',
                                          padding: theme.spacing.xxl,
                                          color: theme.colors.text.muted,
                                          fontSize: '0.875rem'
                                    }}>
                                          {emptyMessage}
                                    </div>
                              ) : (
                                    data.map((item, index) => {
                                          const itemDisplayValues = item?.displayValues || [];
                                          const itemId = item?.id || index;

                                          return (
                                                <div
                                                      key={itemId}
                                                      style={{
                                                            borderBottom: index < data.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                                                            padding: '1rem 1.25rem',
                                                            backgroundColor: index % 2 === 0 ? 'white' : '#fafbfc',
                                                            transition: 'background-color 0.2s ease'
                                                      }}
                                                >
                                                      <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: gridTemplate,
                                                            gap: '1rem',
                                                            fontSize: '0.875rem',
                                                            alignItems: 'center'
                                                      }}>
                                                            {itemDisplayValues.map((value, valueIndex) => (
                                                                  <div
                                                                        key={valueIndex}
                                                                        style={{
                                                                              color: theme.colors.text.primary,
                                                                              lineHeight: '1.5',
                                                                              wordBreak: 'break-word',
                                                                              padding: '0 0.5rem',
                                                                              minWidth: 0,
                                                                              ...getTruncatedStyle(truncateLines)
                                                                        }}
                                                                  >
                                                                        {truncateLines && typeof value === 'string'
                                                                              ? truncateText(value, truncateLines)
                                                                              : value
                                                                        }
                                                                  </div>
                                                            ))}
                                                            <div style={{
                                                                  textAlign: 'right',
                                                                  display: 'flex',
                                                                  gap: '0.5rem',
                                                                  justifyContent: 'flex-end',
                                                                  padding: '0 0.5rem',
                                                                  minWidth: showEditButton ? '120px' : '80px'
                                                            }}>
                                                                  {showEditButton && (
                                                                        <button
                                                                              onClick={() => handleEditItem(itemId)}
                                                                              style={{
                                                                                    color: '#3b82f6',
                                                                                    backgroundColor: 'transparent',
                                                                                    border: `1px solid #3b82f6`,
                                                                                    cursor: 'pointer',
                                                                                    padding: '0.375rem',
                                                                                    borderRadius: '0.375rem',
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    transition: 'all 0.2s ease'
                                                                              }}
                                                                              onMouseOver={(e) => {
                                                                                    e.target.style.backgroundColor = '#3b82f6';
                                                                                    e.target.style.color = 'white';
                                                                              }}
                                                                              onMouseOut={(e) => {
                                                                                    e.target.style.backgroundColor = 'transparent';
                                                                                    e.target.style.color = '#3b82f6';
                                                                              }}
                                                                              title={editButtonLabel}
                                                                        >
                                                                              <Edit size={14} />
                                                                        </button>
                                                                  )}
                                                                  <button
                                                                        onClick={() => handleRemoveItem(itemId)}
                                                                        style={{
                                                                              color: theme.colors.error,
                                                                              backgroundColor: 'transparent',
                                                                              border: `1px solid ${theme.colors.error}`,
                                                                              cursor: 'pointer',
                                                                              padding: '0.375rem',
                                                                              borderRadius: '0.375rem',
                                                                              display: 'inline-flex',
                                                                              alignItems: 'center',
                                                                              justifyContent: 'center',
                                                                              transition: 'all 0.2s ease'
                                                                        }}
                                                                        onMouseOver={(e) => {
                                                                              e.target.style.backgroundColor = theme.colors.error;
                                                                              e.target.style.color = 'white';
                                                                        }}
                                                                        onMouseOut={(e) => {
                                                                              e.target.style.backgroundColor = 'transparent';
                                                                              e.target.style.color = theme.colors.error;
                                                                        }}
                                                                        title={removeButtonLabel}
                                                                  >
                                                                        <X size={14} />
                                                                  </button>
                                                            </div>
                                                      </div>
                                                </div>
                                          );
                                    })
                              )}
                        </div>
                  </div>

                  <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        marginTop: '1rem'
                  }}>
                        <button
                              onClick={() => {
                                    setCurrentItem(initializeFormData());
                                    setEditingItemId(null);
                                    // setOpenHints({}); // removed unused state cleanup
                                    setHoveredHint(null);
                                    setShowForm(!showForm);
                              }}
                              style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    minHeight: '40px'
                              }}
                              onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#2563eb';
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                              }}
                              onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#3b82f6';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                              }}
                        >
                              <Plus size={16} />
                              {addButtonText}
                        </button>
                  </div>

                  {showForm && (
                        <div style={{
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '0.5rem',
                              padding: '1.5rem',
                              marginTop: '1.5rem'
                        }}>
                              <div style={{
                                    marginBottom: '1rem',
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#1f2937'
                              }}>
                                    {editingItemId !== null ? 'Edit Item' : 'Add New Item'}
                              </div>

                              {fields.map(field => renderField(field))}

                              {/* Photo Upload Section with Item Index */}
                              {enablePhotoUpload && (
                                    <PhotoUpload
                                          photos={currentItem.photos || []}
                                          onPhotosChange={(photos) => handleInputChange('photos', photos)}
                                          referenceNo={referenceNo}
                                          sectionName={sectionName}
                                          itemIndex={getItemIndex()}
                                    />
                              )}

                              <div style={{
                                    display: 'flex',
                                    gap: '0.75rem',
                                    justifyContent: 'flex-end',
                                    marginTop: '1.5rem'
                              }}>
                                    <button
                                          onClick={handleCancelForm}
                                          style={{
                                                padding: '0.5rem 1rem',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '0.375rem',
                                                backgroundColor: 'white',
                                                color: '#374151',
                                                fontSize: '0.875rem',
                                                cursor: 'pointer',
                                                minHeight: '40px',
                                                transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#f9fafb';
                                                e.target.style.borderColor = '#9ca3af';
                                          }}
                                          onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'white';
                                                e.target.style.borderColor = '#d1d5db';
                                          }}
                                    >
                                          Cancel
                                    </button>
                                    <button
                                          onClick={handleAddItem}
                                          style={{
                                                padding: '0.5rem 1rem',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                backgroundColor: '#10b981',
                                                color: 'white',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                minHeight: '40px',
                                                transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#059669';
                                                e.target.style.transform = 'translateY(-1px)';
                                          }}
                                          onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = '#10b981';
                                                e.target.style.transform = 'translateY(0)';
                                          }}
                                    >
                                          {editingItemId !== null ? 'Update Item' : 'Add Item'}
                                    </button>
                              </div>
                        </div>
                  )}

                  {/* Global Hint Modal - Centered and Always on Top */}
                  {hoveredHint && (
                        <div style={{
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0, 0, 0, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 999999,
                              pointerEvents: "none"
                        }}>
                              <div style={{
                                    backgroundColor: "#1f2937",
                                    color: "white",
                                    padding: "1rem",
                                    borderRadius: "0.5rem",
                                    fontSize: "0.875rem",
                                    lineHeight: "1.4",
                                    boxShadow: "0 20px 25px rgba(0, 0, 0, 0.3)",
                                    width: "300px",
                                    maxWidth: "90vw",
                                    maxHeight: "80vh",
                                    overflow: "auto",
                                    pointerEvents: "auto"
                              }}>
                                    {(() => {
                                          // Find the current field to get its hint
                                          const currentField = fields.find(f => f.name === hoveredHint) ||
                                                fields.flatMap(f => f.type === 'grid' ? f.fields : []).find(f => f.name === hoveredHint);

                                          if (!currentField?.hint) return null;

                                          return typeof currentField.hint === 'string' ? (
                                                <div>{currentField.hint}</div>
                                          ) : (
                                                currentField.hint
                                          );
                                    })()}

                                    <div style={{
                                          marginTop: "0.75rem",
                                          textAlign: "right"
                                    }}>
                                          <button
                                                onClick={() => setHoveredHint(null)}
                                                style={{
                                                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                                                      color: "white",
                                                      border: "none",
                                                      padding: "0.25rem 0.5rem",
                                                      borderRadius: "0.25rem",
                                                      fontSize: "0.75rem",
                                                      cursor: "pointer"
                                                }}
                                          >
                                                Close
                                          </button>
                                    </div>
                              </div>
                        </div>
                  )}
            </div>
      );
});

export default ModernDataTable;